import { createUniqueId, Show, splitProps, type Component, type JSX } from "solid-js";

export type AplombFieldType =
  | "text"
  | "email"
  | "tel"
  | "url"
  | "postal-fsa"
  | "neq"
  | "password";

export interface AplombFieldProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  type?: AplombFieldType;
  helper?: string;
  error?: string;
  required?: boolean;
}

const TYPE_TO_INPUT_TYPE: Record<AplombFieldType, string> = {
  text: "text",
  email: "email",
  tel: "tel",
  url: "url",
  "postal-fsa": "text",
  neq: "text",
  password: "password",
};

const TYPE_TO_INPUT_MODE: Record<AplombFieldType, string | undefined> = {
  text: undefined,
  email: "email",
  tel: "tel",
  url: "url",
  "postal-fsa": undefined,
  neq: "numeric",
  password: undefined,
};

const TYPE_TO_MAX: Record<AplombFieldType, number | undefined> = {
  "postal-fsa": 3,
  neq: 10,
  text: undefined,
  email: undefined,
  tel: undefined,
  url: undefined,
  password: undefined,
};

export const AplombField: Component<AplombFieldProps> = (rawProps) => {
  const [local, rest] = splitProps(rawProps, [
    "label",
    "type",
    "helper",
    "error",
    "required",
    "id",
    "class",
  ]);
  const inputId = local.id ?? `a-field-${createUniqueId()}`;
  const helperId = () => (local.helper ? `${inputId}-h` : undefined);
  const errorId = () => (local.error ? `${inputId}-e` : undefined);
  const type = () => local.type ?? "text";

  return (
    <div class={`a-field${local.error ? " a-field--error" : ""}${local.class ? ` ${local.class}` : ""}`}>
      <label for={inputId} class="a-field__label">
        {local.label}
        <Show when={local.required}>
          {" "}
          <span aria-hidden="true">*</span>
        </Show>
      </label>
      <input
        id={inputId}
        class="a-field__input"
        type={TYPE_TO_INPUT_TYPE[type()]}
        inputmode={TYPE_TO_INPUT_MODE[type()] as JSX.HTMLAttributes<HTMLInputElement>["inputmode"]}
        maxlength={TYPE_TO_MAX[type()]}
        aria-invalid={local.error ? "true" : undefined}
        aria-describedby={[helperId(), errorId()].filter(Boolean).join(" ") || undefined}
        aria-required={local.required ? "true" : undefined}
        {...rest}
      />
      <Show when={local.helper && !local.error}>
        <p id={helperId()} class="a-field__helper">
          {local.helper}
        </p>
      </Show>
      <Show when={local.error}>
        <p id={errorId()} class="a-field__error" role="alert">
          {local.error}
        </p>
      </Show>
    </div>
  );
};
